import { useQuery } from '@tanstack/react-query';
import type { ICareHome } from '../../interfaces';
import type { IAccount } from '../../interfaces/Account/IAccount.ts';
import type { IStation } from '../../interfaces/Station/IStation.ts';
import { STALE_TIME_DETAIL_MS, STALE_TIME_LIST_MS } from '../../constants/reactQueryStaleTime.ts';
import AccountService from '../../services/AccountService.ts';
import CareHomeService from '../../services/CareHomeService.ts';
import StationService from '../../services/StationService.ts';

// React Query keys for CareHomePage (centralized for consistency)
export const getCareHomeQueryKey = (careHomeId?: string) =>
  ['careHome', careHomeId] as const;
export const getStationsQueryKey = (careHomeId?: string) =>
  ['stations', careHomeId] as const;
export const getCockpitUsersQueryKey = (careHomeId?: string) =>
  ['cockpitUsers', careHomeId] as const;

export function useCareHomeQuery(careHomeId?: string) {
  return useQuery<ICareHome>({
    // Care home detail (cached)
    queryKey: getCareHomeQueryKey(careHomeId),
    enabled: !!careHomeId,
    queryFn: () => CareHomeService.getCareHome(careHomeId!),
    staleTime: STALE_TIME_DETAIL_MS,
  });
}

export function useStationsQuery(careHomeId?: string) {
  return useQuery<IStation[]>({
    // Stations list for the current care home (cached)
    queryKey: getStationsQueryKey(careHomeId),
    enabled: !!careHomeId,
    staleTime: STALE_TIME_LIST_MS,
    queryFn: async () => {
      const response = await StationService.getListStations(
        1,
        100,
        '',
        careHomeId!,
        'true',
        'name',
        'asc'
      );
      return response.items ?? [];
    },
  });
}

export function useCockpitUsersQuery(careHomeId?: string) {
  return useQuery<IAccount[]>({
    // Cockpit users for the current care home (cached)
    queryKey: getCockpitUsersQueryKey(careHomeId),
    enabled: !!careHomeId,
    staleTime: STALE_TIME_LIST_MS,
    queryFn: async () => {
      const response = await AccountService.getAccounts(
        1,
        100,
        undefined,
        careHomeId!,
        'Care home management',
        undefined,
        'last_name',
        'asc'
      );
      return response.items ?? [];
    },
  });
}

